import { useEffect, useMemo, useRef, useState } from "react";
import api, { Glossary } from "../../../../hooks/api";
import classNames from "./GlossarySetting.module.css";
import { Setting } from "./Settings";
import { useAxios } from "../../../../hooks/axios";
import Loader from "../../../common/Loader";
import { getLanguageFlag, getLanguageName } from "../../../../types";
import { IoClose, IoInformationCircleOutline } from "react-icons/io5";
import LanguageMultiSelect from "../../../common/LanguageMultiSelect";
import Button from "../../../common/Button";
import { IoMdAdd } from "react-icons/io";
import ConfirmActionDialog from "../../../common/dialogs/ConfirmActionDialog";

const ConfirmDeleteGlossaryDialog = (props: {
  language: string;
  onConfirm: () => void;
}) => {
  const [open, setOpen] = useState(false);

  return (
    <ConfirmActionDialog
      open={open}
      onOpenChange={setOpen}
      trigger={
        <button onClick={() => setOpen(true)}>
          <IoClose />
        </button>
      }
      title={"Delete glossary"}
      action="Delete"
      content={
        <h3 className={classNames.ConfirmDelete}>
          Are you sure you want to delete the glossary for{" "}
          <span>{getLanguageName(props.language)}</span>?
        </h3>
      }
      onConfirm={() => {
        props.onConfirm();
        setOpen(false);
      }}
    />
  );
};

function EditGlossary({
  glossary,
  onUpdate,
  onDelete,
}: {
  glossary: Glossary;
  onUpdate: (updatedGlossary: Glossary) => void;
  onDelete: (deletedGlossary: Glossary) => void;
}) {
  const phraseInputRef = useRef<HTMLTextAreaElement | null>(null);

  const [newPhrase, setNewPhrase] = useState("");

  return (
    <div key={glossary.id} className={classNames.EditGlossary}>
      <div>
        <div className={classNames.Title}>Language</div>
        <div className={classNames.EditGlossaryLanguage}>
          {getLanguageFlag(glossary.language)}{" "}
          {getLanguageName(glossary.language)}
        </div>
      </div>
      <div>
        <div className={`${classNames.Title} ${classNames.EditGlossaryTitle}`}>
          Glossary in {getLanguageName(glossary.language)}
          <ConfirmDeleteGlossaryDialog
            language={glossary.language}
            onConfirm={() => {
              onDelete(glossary);
            }}
          />
        </div>
        <div
          className={classNames.EditGlossaryPhrases}
          onClick={() => phraseInputRef.current?.focus()}
        >
          {glossary.phrases.map((phrase, index) => (
            <div key={index}>
              {phrase}
              <button
                onClick={() => {
                  const newPhrases = [...glossary.phrases];
                  // Remove phrase
                  newPhrases.splice(index, 1);
                  onUpdate({
                    ...glossary,
                    phrases: newPhrases,
                  });
                }}
              >
                <IoClose />
              </button>
            </div>
          ))}
          <textarea
            ref={phraseInputRef}
            value={newPhrase}
            onChange={(e) => {
              // If user copy/pasted
              const value = e.target.value;
              if (value.includes(",") || value.includes("\n")) {
                onUpdate({
                  ...glossary,
                  phrases: [
                    ...glossary.phrases,
                    ...value
                      .split(/[\n,]+/)
                      .map((phrase) => phrase.trim())
                      .filter((phrase) => !!phrase.length)
                      .slice(0, 500 - glossary.phrases.length),
                  ],
                });
                setNewPhrase("");
              } else {
                setNewPhrase(value);
              }
            }}
          />
        </div>
        <div className={classNames.Footer}>
          <div className={classNames.Tip}>
            <IoInformationCircleOutline />
            Enter or paste in words separated by comma or new lines
          </div>
          <div>{glossary.phrases.length}/500</div>
        </div>
      </div>
    </div>
  );
}

export default function GlossarySetting() {
  const [glossaries, setGlossaries] = useState<Glossary[]>();
  const axios = useAxios();
  const getGlossaryList = api.useGetGlossaryList(axios);
  const createGlossary = api.useCreateGlossary(axios);
  const updateGlossary = api.useUpdateGlossary(axios);
  const deleteGlossary = api.useDeleteGlossary(axios);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const response = await getGlossaryList();
        setGlossaries(response.data.glossaries);
      } finally {
        setLoading(false);
      }
    })();
  }, [getGlossaryList]);

  const languages = useMemo(() => {
    if (!glossaries) {
      return [];
    }
    return glossaries.map((g) => g.language);
  }, [glossaries]);

  if (!glossaries || loading) {
    return <Loader />;
  }

  return (
    <Setting
      title="Glossary"
      subtitle="Please provide a list of technical terminologies and proper nouns for model fine-tuning"
      className={classNames.GlossarySetting}
    >
      <div className={classNames.Glossaries}>
        <LanguageMultiSelect
          trigger={
            <Button
              className={classNames.AddLanguageTrigger}
              backgroundColor="black"
            >
              <IoMdAdd />
              Add language
            </Button>
          }
          selectedLanguages={languages}
          onChange={async (newLanguages) => {
            newLanguages.forEach(async (lang) => {
              if (!languages.includes(lang)) {
                const response = await createGlossary({
                  language: lang,
                  phrases: [],
                });
                setGlossaries((prevState) => [
                  ...prevState!,
                  response.data.glossary,
                ]);
              }
            });
          }}
        />
        {/* Copy paste/inline for both commas newlines */}
        {glossaries.map((glossary) => (
          <EditGlossary
            key={glossary.id}
            glossary={glossary}
            onUpdate={async (updated) => {
              setGlossaries((prevState) =>
                prevState!.map((g) => (g.id === updated.id ? updated : g))
              );
              await updateGlossary(updated);
            }}
            onDelete={async () => {
              setGlossaries((prevState) =>
                prevState!.filter((g) => g.id !== glossary.id)
              );
              await deleteGlossary(glossary);
            }}
          />
        ))}
      </div>
    </Setting>
  );
}
